import React, { useEffect, useState, useDeferredValue } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../../context/authContext';
import { ordenarFranjas } from '../../../utils/franjas';
import AuthImage, { abrirFicheroProtegido, fetchProtegido } from "../../../components/AuthImage";
import BotonVolver from "../../../components/BotonVolver";

const estadoColores = { pedido:'#EE82EE', comprado:'#3b82f6', 'en reparto':'#f59e0b', entregado:'#10b981', 'en la puerta':'#d12b39ff' };
const estadoOrder = ['pedido','comprado','en reparto','entregado','en la puerta'];

function hexToRgb(hex){ if(!hex) return null; let h=hex.replace('#','').trim(); if(h.length===3) h=h.split('').map(c=>c+c).join(''); if(h.length===8) h=h.slice(0,6); const n=parseInt(h,16); if(Number.isNaN(n)) return null; return {r:(n>>16)&255,g:(n>>8)&255,b:n&255}; }
function getEstadoColors(e){ const hex=estadoColores[e]; const rgb=hexToRgb(hex); if(!rgb) return {bg:undefined,border:undefined,text:undefined}; const {r,g,b}=rgb; return { bg:`rgba(${r},${g},${b},0.12)`, border:hex, text:hex }; }

const inputStyleBase = { padding:'8px 12px', fontSize:'14px', height:'40px', boxSizing:'border-box', border:'1px solid #ccc', borderRadius:'4px', outline:'none', fontFamily:'inherit', flexShrink:0, minWidth:'140px' };

// Helpers para colores de rutas (consistente con otros módulos)
const coloresRutas = [
  "#4ECDC4",
  "#4CAF50",
  "#556270",
  "#C7F464",
  "#C44DFF",
  "#FFB347"
];
const getContrastColor = (hex) => {
  if (!hex) return '#000';
  const c = hex.replace('#','');
  const r = parseInt(c.substring(0,2),16);
  const g = parseInt(c.substring(2,4),16);
  const b = parseInt(c.substring(4,6),16);
  const lum = 0.2126*r + 0.7152*g + 0.0722*b;
  return lum > 150 ? '#000' : '#fff';
};
const darkenColor = (hex, percent) => {
  if (!hex) return hex;
  const c = hex.replace('#','');
  const num = parseInt(c,16);
  let r = (num >> 16) & 0xFF;
  let g = (num >> 8) & 0xFF;
  let b = num & 0xFF;
  r = Math.max(0, Math.min(255, Math.round(r * (100 - percent) / 100)));
  g = Math.max(0, Math.min(255, Math.round(g * (100 - percent) / 100)));
  b = Math.max(0, Math.min(255, Math.round(b * (100 - percent) / 100)));
  const rr = r.toString(16).padStart(2,'0');
  const gg = g.toString(16).padStart(2,'0');
  const bb = b.toString(16).padStart(2,'0');
  return `#${rr}${gg}${bb}`;
};

// Normaliza cadenas para comparación (quita diacríticos, trim y lowercase)
function normalizeName(str){ try { return (str||'').toString().normalize('NFD').replace(/\p{Diacritic}/gu,'').toLowerCase().trim(); } catch(e) { return (str||'').toString().toLowerCase().trim(); } }
const VerComprasAdmin = () => {
  const { user } = useAuth(); // auth check if needed

  // Datos paginados
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Tamaño de página por defecto a 50; en modo ordenación global se usa 500 por petición hasta completar todo
  const pageSize = 50;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketsVisibles, setTicketsVisibles] = useState(()=> new Set());
  const [fotosPuertaVisibles, setFotosPuertaVisibles] = useState(()=> new Set());
  const [countsByEstado, setCountsByEstado] = useState({});

  // Filtros
  const [filtroAbreviatura, setFiltroAbreviatura] = useState('');
  const [filtroSupervisora, setFiltroSupervisora] = useState('');
  const [filtroComprador, setFiltroComprador] = useState('');
  const [filtroTicket, setFiltroTicket] = useState(''); // '' = todos, 'with' = ticket subido, 'without' = ticket pendiente
  const [filtroRepartidor, setFiltroRepartidor] = useState('');
  const [filtroRuta, setFiltroRuta] = useState('');
  const [filtroSupermercado, setFiltroSupermercado] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFranja, setFiltroFranja] = useState('');
  const [sort, setSort] = useState(''); // '', 'dia_compra_desc', 'dia_reparto_desc'
  const [fechaPedidoInicio, setFechaPedidoInicio] = useState('');
  const [fechaPedidoFin, setFechaPedidoFin] = useState('');
  const [fechaCompraInicio, setFechaCompraInicio] = useState('');
  const [fechaCompraFin, setFechaCompraFin] = useState('');
  const [fechaRepartoInicio, setFechaRepartoInicio] = useState('');
  const [fechaRepartoFin, setFechaRepartoFin] = useState('');

  const [mostrarFiltrosMovil, setMostrarFiltrosMovil] = useState(false);

  const defAbrev = useDeferredValue(filtroAbreviatura);

  // Facetas globales
  const [facets, setFacets] = useState({ supervisoras: [], compradores: [], repartidores: [], rutas: [], estados: [], franjas: [], supermercados: [] });
  // Por defecto, compradores/repartidores del filtro solo incluyen los activos.
  const [tambienInactivos, setTambienInactivos] = useState(false);
  useEffect(()=>{
    (async()=>{
      try{
        const url = `/api/admin/verCompras/facetas${tambienInactivos ? '?incluir_inactivos=1' : ''}`;
        const r = await axios.get(url);
        const f = r.data || {};
        setFacets({
          supervisoras: Array.isArray(f.supervisoras)? f.supervisoras:[],
          compradores: Array.isArray(f.compradores)? f.compradores:[],
          repartidores: Array.isArray(f.repartidores)? f.repartidores:[],
          rutas: Array.isArray(f.rutas)? f.rutas:[],
          estados: Array.isArray(f.estados)? f.estados:[],
          franjas: Array.isArray(f.franjas)? f.franjas:[],
          supermercados: Array.isArray(f.supermercados)? f.supermercados:[],
        });
      }catch(e){ /* silencioso, se usarán opciones locales como fallback */ }
    })();
  },[tambienInactivos]);

  const fetchPage = async (targetPage = page, keepScroll=false) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', targetPage);
      params.set('pageSize', pageSize);
      if (defAbrev) params.set('abreviatura', defAbrev);
      if (filtroSupervisora) params.set('supervisora', filtroSupervisora);
      if (filtroComprador) params.set('comprador', filtroComprador);
      if (filtroRepartidor) params.set('repartidor', filtroRepartidor);
      if (filtroRuta) params.set('ruta', filtroRuta);
  if (filtroEstado) params.set('estado', filtroEstado);
  if (filtroSupermercado) params.set('supermercado', filtroSupermercado);
      if (filtroFranja) params.set('franja', filtroFranja);
      if (filtroTicket) params.set('ticket', filtroTicket);
      if (fechaPedidoInicio) params.set('diaPedidoInicio', fechaPedidoInicio);
      if (fechaPedidoFin) params.set('diaPedidoFin', fechaPedidoFin);
      if (fechaCompraInicio) params.set('diaCompraInicio', fechaCompraInicio);
      if (fechaCompraFin) params.set('diaCompraFin', fechaCompraFin);
  if (fechaRepartoInicio) params.set('diaRepartoInicio', fechaRepartoInicio);
  if (fechaRepartoFin) params.set('diaRepartoFin', fechaRepartoFin);
  if (sort) params.set('sort', sort);
      const url = `/api/admin/verCompras/compras?${params.toString()}`;
      const res = await axios.get(url);
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(Number(res.data?.total || 0));
  setCountsByEstado(res.data?.countsByEstado || {});
      setPage(Number(res.data?.page || targetPage));
      if (!keepScroll) {
        window.scrollTo({ top:0 });
      }
    } catch (e) {
      setError('❌ Error al cargar las compras');
      setItems([]); setTotal(0);
    } finally { setLoading(false); setInitialLoading(false); }
  };

  // Inicial
  useEffect(()=>{ fetchPage(1); /* eslint-disable-next-line */ }, []);
  // Refetch al cambiar filtros (debounced solo abreviatura via deferred)
  useEffect(()=>{ const t = setTimeout(()=> fetchPage(1), 150); return ()=> clearTimeout(t); }, [defAbrev, filtroSupervisora, filtroComprador, filtroRepartidor, filtroRuta, filtroSupermercado, filtroEstado, filtroFranja, filtroTicket, fechaPedidoInicio, fechaPedidoFin, fechaCompraInicio, fechaCompraFin, fechaRepartoInicio, fechaRepartoFin, sort]);

  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return ''; const iso = fechaStr.replace(' ', 'T'); const d = new Date(iso); if (isNaN(d)) return fechaStr; const dd=String(d.getDate()).padStart(2,'0'); const mm=String(d.getMonth()+1).padStart(2,'0'); const yyyy=d.getFullYear(); const hh=String(d.getHours()).padStart(2,'0'); const mi=String(d.getMinutes()).padStart(2,'0'); return `${dd}-${mm}-${yyyy} ${hh}:${mi}`; };

  const descargarCompraPDF = (compra) => {
    try {
      const pageHeight = 297; // A4 en mm
      const marginTop = 20;
      const lineHeight = 10;
      const bottomMargin = 20;
      const maxY = pageHeight - bottomMargin;

      const doc = new jsPDF();
      doc.setFontSize(18);
      let y = marginTop;
      doc.setFont('helvetica','bold');

      let abreviatura_piso_sin_cajetin = (compra.abreviatura_piso || '').split("(")[0];
      const info_piso = `Piso ID: ${compra.id_piso} - ${abreviatura_piso_sin_cajetin}`;
      doc.text(info_piso, 10, y); y += 15;

      doc.setFontSize(14);
      const fechaPedido = compra.dia_pedido || '';
      doc.text(`Fecha pedido: ${fechaPedido}`, 10, y); y += lineHeight;
      doc.text(`Franja horaria: ${compra.franja_horaria || '-'}`, 10, y); y += lineHeight;
      if (compra.user_supervisora) { doc.text(`Supervisora: ${compra.user_supervisora}`, 10, y); y += lineHeight; }

      if (compra.notas_supervisora && String(compra.notas_supervisora).trim() !== '') {
        y += 4;
        doc.text('Notas de la supervisora:', 10, y);
        y += 8;
        const maxWidth = 180;
        const paddingH = 5;
        const paddingW = 5;
        const notasWrapped = doc.splitTextToSize(String(compra.notas_supervisora), maxWidth - paddingW*2);
        const blockHeight = notasWrapped.length * lineHeight + paddingH * 2;
        if (y + blockHeight > maxY) { doc.addPage(); y = marginTop; }
        doc.rect(10, y, maxWidth, blockHeight);
        doc.setFont('helvetica','normal');
        let textY = y + paddingH + 2;
        notasWrapped.forEach(linea => { if (textY > maxY) { doc.addPage(); textY = marginTop; } doc.text(linea, 10 + paddingW, textY); textY += lineHeight; });
        y += blockHeight + 6;
      } else {
        doc.setFont('helvetica','normal');
      }

      y += lineHeight;
      (compra.productos || []).slice().sort((a,b)=> (a.nombre||'').localeCompare(b.nombre||''))
        .forEach(p => { if (y > maxY) { doc.addPage(); y = marginTop; } doc.text(`- ${p.nombre} (Cantidad: ${p.cantidad})`, 10, y); y += lineHeight; });

      const fechaFormateada = String(fechaPedido).replace(/\//g,'-').replace(/[: ]/g,'_');
      doc.save(`Compra_${compra.id}_${compra.abreviatura_piso}_${fechaFormateada}.pdf`);
    } catch (e) { alert('No se pudo generar el PDF de la compra.'); }
  };

  const opcionesSupervisora = (facets.supervisoras.length? facets.supervisoras : [...new Set(items.map(c=>c.user_supervisora).filter(Boolean))]).sort();
  const opcionesComprador = (facets.compradores.length? facets.compradores : [...new Set(items.map(c=>c.user_comprador).filter(Boolean))]).sort();
  const opcionesRepartidor = (facets.repartidores.length? facets.repartidores : [...new Set(items.map(c=>c.user_repartidor).filter(Boolean))]).sort();
  const opcionesRutas = (facets.rutas.length? facets.rutas : [...new Set(items.map(c=> c.ruta_piso).filter(Boolean))]).sort();
  const opcionesSupermercado = (facets.supermercados.length? facets.supermercados : [...new Set(items.map(c=> c.supermercado).filter(s=> s && String(s).trim()!=='-'))]).sort();
  const opcionesEstados = (facets.estados.length? facets.estados : [...new Set(items.map(c=> c.estado).filter(Boolean))]).sort();
  const opcionesFranjas = ordenarFranjas(facets.franjas.length? facets.franjas : [...new Set(items.map(c=> c.franja_horaria).filter(Boolean))]);

  // Normalizar y ordenar rutas únicas usadas para asignar colores estables
  const rutasUnicas = Array.from(new Set(opcionesRutas.map(r => String(r).trim()).filter(s => s !== ''))).sort((a,b)=> a.localeCompare(b, undefined, { numeric: true }));
  const obtenerColorRuta = (ruta) => {
    if (ruta === null || ruta === undefined || String(ruta).trim() === '') return '#888';
    const rutaStr = String(ruta);
    const index = rutasUnicas.findIndex(x => String(x) === rutaStr);
    if (index === -1) return '#888';
    return coloresRutas[index % coloresRutas.length] || '#888';
  };

  if (initialLoading) return <p>Cargando compras...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'20px', paddingBottom:'90px', fontSize:'17px', fontFamily:'Arial, sans-serif' }}>
      <style>{`details summary::-webkit-details-marker { display: none; } details summary::marker { display: none; } details summary { position: relative; } details.cambios-details summary::before { content: '▶'; position: absolute; left: 0; transition: transform 0.2s; font-size: 11px; } details.cambios-details[open] summary::before { transform: rotate(90deg); }`}</style>
      <BotonVolver fallback="/admin" />
      {/* Botón móvil filtros */}
      <button
        onClick={()=> setMostrarFiltrosMovil(p=>!p)}
        style={{ display:'none', padding:'10px 20px', fontSize:16, background:'#b294d6', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', marginBottom:15, fontWeight:600, width:'100%' }}
        className='boton-movil-filtros'
        aria-expanded={mostrarFiltrosMovil}
        aria-controls='filtros-admin-container'
      >{mostrarFiltrosMovil ? '❌ Ocultar filtros' : '🔍 Mostrar filtros'}</button>

      <h2 style={{ textAlign:'center', fontSize:'26px', marginBottom:'14px', color:'#333' }}>Ver Compras (Admin)</h2>

      {/* Envolver filtros para poder ocultarlos en móvil */}
      <div id='filtros-admin-container' style={{ display:(window.innerWidth<=768 && !mostrarFiltrosMovil)?'none':'flex', flexWrap:'wrap', gap:10, marginBottom:22, alignItems:'center' }}>
        {/* Filtros */}
        <input style={inputStyleBase} placeholder='Abreviatura' value={filtroAbreviatura} onChange={e=>setFiltroAbreviatura(e.target.value)} />
        <select style={inputStyleBase} value={filtroSupervisora} onChange={e=>setFiltroSupervisora(e.target.value)}>
          <option value=''>Todas supervisoras</option>
          {opcionesSupervisora.map(s=> <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={inputStyleBase} value={filtroComprador} onChange={e=>setFiltroComprador(e.target.value)}>
          <option value=''>Todos compradores</option>
          {opcionesComprador.map(s=> <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={inputStyleBase} value={filtroRepartidor} onChange={e=>setFiltroRepartidor(e.target.value)}>
          <option value=''>Todos repartidores</option>
          {opcionesRepartidor.map(s=> <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          type='button'
          onClick={()=> setTambienInactivos(v=>!v)}
          style={{
            padding:'8px 14px', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer',
            border: tambienInactivos ? '1.5px solid #7c3aed' : '1.5px solid #ccc',
            background: tambienInactivos ? '#7c3aed' : '#fff',
            color: tambienInactivos ? '#fff' : '#555',
          }}
        >
          {tambienInactivos ? '✓ También inactivos' : 'También inactivos'}
        </button>
        <select
          style={{
            ...inputStyleBase,
            background: filtroRuta ? obtenerColorRuta(filtroRuta) : undefined,
            color: filtroRuta ? getContrastColor(obtenerColorRuta(filtroRuta)) : undefined,
            border: filtroRuta ? `1px solid ${darkenColor(obtenerColorRuta(filtroRuta), 25)}` : inputStyleBase.border,
          }}
          value={filtroRuta}
          onChange={e=>setFiltroRuta(e.target.value)}
        >
          <option value=''>Todas rutas</option>
          {rutasUnicas.map(r=> <option key={r} value={r} style={{ backgroundColor: obtenerColorRuta(r), color: getContrastColor(obtenerColorRuta(r)) }}>{r}</option>)}
        </select>
        <select style={inputStyleBase} value={filtroSupermercado} onChange={e=>setFiltroSupermercado(e.target.value)}>
          <option value=''>Todos supermercados</option>
          {opcionesSupermercado.map(s=> <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filtroEstado}
          onChange={e=>setFiltroEstado(e.target.value)}
          style={{ ...inputStyleBase, fontWeight:700, color: filtroEstado ? (getEstadoColors(filtroEstado).text||'#000'):undefined, backgroundColor: filtroEstado? getEstadoColors(filtroEstado).bg:undefined, border: filtroEstado? `2px solid ${getEstadoColors(filtroEstado).border}`:inputStyleBase.border }}>
          <option value=''>Todos estados</option>
          {estadoOrder.map(est=> <option key={est} value={est}>{est}</option>)}
        </select>
        <select style={inputStyleBase} value={filtroFranja} onChange={e=>setFiltroFranja(e.target.value)}>
          <option value=''>Todas franjas</option>
          {opcionesFranjas.map(f=> <option key={f} value={f}>{f}</option>)}
        </select>
        <select
          style={{
            ...inputStyleBase,
            background: filtroTicket === 'with' ? '#5beca9ff' : (filtroTicket === 'without' ? '#e6616aff' : inputStyleBase.background),
            color: filtroTicket ? getContrastColor(filtroTicket === 'with' ? '#5beca9ff' : '#e6616aff') : undefined,
            border: filtroTicket ? `1px solid ${darkenColor(filtroTicket === 'with' ? '#5beca9ff' : '#e6616aff', 25)}` : inputStyleBase.border,
          }}
          value={filtroTicket}
          onChange={e=>setFiltroTicket(e.target.value)}
        >
          <option value=''>Todos los tickets</option>
          <option value='with' style={{ backgroundColor: '#5beca9ff', color: getContrastColor('#5beca9ff') }}>ticket subido</option>
          <option value='without' style={{ backgroundColor: '#e6616aff', color: getContrastColor('#e6616aff') }}>ticket pendiente</option>
        </select>
        {/* Fechas con etiquetas */}
        <div style={{ display:'flex', flexDirection:'column', gap:4, background:'#f9f9f9', padding:4, borderRadius:6 }}>
          <span style={{ fontSize:12, fontWeight:700, letterSpacing:.5 }}>Pedido</span>
          <div style={{ display:'flex', gap:6 }}>
            <input type='date' style={inputStyleBase} value={fechaPedidoInicio} onChange={e=>setFechaPedidoInicio(e.target.value)} title='Pedido desde' />
            <input type='date' style={inputStyleBase} value={fechaPedidoFin} onChange={e=>setFechaPedidoFin(e.target.value)} title='Pedido hasta' />
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:4, background:'#f9f9f9', padding:4, borderRadius:6 }}>
          <span style={{ fontSize:12, fontWeight:700, letterSpacing:.5 }}>Compra</span>
          <div style={{ display:'flex', gap:6 }}>
            <input type='date' style={inputStyleBase} value={fechaCompraInicio} onChange={e=>setFechaCompraInicio(e.target.value)} title='Compra desde' />
            <input type='date' style={inputStyleBase} value={fechaCompraFin} onChange={e=>setFechaCompraFin(e.target.value)} title='Compra hasta' />
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:4, background:'#f9f9f9', padding:4, borderRadius:6 }}>
          <span style={{ fontSize:12, fontWeight:700, letterSpacing:.5 }}>Reparto</span>
          <div style={{ display:'flex', gap:6 }}>
            <input type='date' style={inputStyleBase} value={fechaRepartoInicio} onChange={e=>setFechaRepartoInicio(e.target.value)} title='Reparto desde' />
            <input type='date' style={inputStyleBase} value={fechaRepartoFin} onChange={e=>setFechaRepartoFin(e.target.value)} title='Reparto hasta' />
          </div>
        </div>
        <button
          onClick={()=> { setFiltroAbreviatura(''); setFiltroSupervisora(''); setFiltroComprador(''); setFiltroRepartidor(''); setFiltroRuta(''); setFiltroSupermercado(''); setFiltroEstado(''); setFiltroFranja(''); setFiltroTicket(''); setFechaPedidoInicio(''); setFechaPedidoFin(''); setFechaCompraInicio(''); setFechaCompraFin(''); setFechaRepartoInicio(''); setFechaRepartoFin(''); setSort(''); }}
          style={{ ...inputStyleBase, cursor:'pointer', background:'#fff', fontWeight:700, border:'4px solid #000', borderRadius:'999px', padding:'10px 18px', boxShadow:'0 4px 0 #000' }}
        >🧹 Limpiar</button>
      </div>
      {/* Ordenación */}
      <div style={{ display:'flex', gap:10, justifyContent:'center', margin:'8px 0 12px' }}>
        <button
          onClick={()=> setSort(prev => prev==='dia_compra_desc' ? 'dia_compra_asc' : prev==='dia_compra_asc' ? '' : 'dia_compra_desc')}
          style={{ padding:'8px 14px', borderRadius:8, border:'2px solid #1565c0', background: (sort==='dia_compra_desc'||sort==='dia_compra_asc') ? '#1565c0' : '#fff', color: (sort==='dia_compra_desc'||sort==='dia_compra_asc') ? '#fff' : '#1565c0', fontWeight:800, cursor:'pointer' }}
          title={sort==='dia_compra_desc' ? 'Ordenar de menor a mayor por día de compra' : sort==='dia_compra_asc' ? 'Quitar orden por compra' : 'Ordenar de mayor a menor por día de compra'}
        >🗓️ Ordenar compra {sort==='dia_compra_desc' ? '↓' : sort==='dia_compra_asc' ? '↑' : '↕'}</button>
        <button
          onClick={()=> setSort(prev => prev==='dia_reparto_desc' ? 'dia_reparto_asc' : prev==='dia_reparto_asc' ? '' : 'dia_reparto_desc')}
          style={{ padding:'8px 14px', borderRadius:8, border:'2px solid #2e7d32', background: (sort==='dia_reparto_desc'||sort==='dia_reparto_asc') ? '#2e7d32' : '#fff', color: (sort==='dia_reparto_desc'||sort==='dia_reparto_asc') ? '#fff' : '#2e7d32', fontWeight:800, cursor:'pointer' }}
          title={sort==='dia_reparto_desc' ? 'Ordenar de menor a mayor por día de entrega' : sort==='dia_reparto_asc' ? 'Quitar orden por entrega' : 'Ordenar de mayor a menor por día de entrega'}
        >📦 Ordenar entrega {sort==='dia_reparto_desc' ? '↓' : sort==='dia_reparto_asc' ? '↑' : '↕'}</button>
      </div>

      {/* Resumen de compras totales y por estado */}
      <div style={{
        width: '100%',
        margin: '0 auto 18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <span style={{
          color:'#1976d2',
          background:'#e3f2fd',
          borderRadius:10,
          padding:'6px 22px',
          border:'1.5px solid #90caf9',
          fontSize: '18px',
          fontWeight: 800,
          letterSpacing:'1px',
          marginBottom: 6,
          textTransform:'uppercase',
          boxShadow:'0 1px 6px #1976d211',
        }}>TOTAL: {total}</span>
        <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center',
          fontSize: 14,
          fontWeight: 600,
        }}>
          {['pedido','comprado','en reparto','entregado','en la puerta'].map((estado) => {
            const count = (countsByEstado && countsByEstado[estado] != null) ? countsByEstado[estado] : items.filter(c => c.estado === estado).length;
            if (!count) return null;
            // Colores más suaves para fondo y texto
            let bg, fg;
            switch(estado) {
              case 'pedido': bg = '#ffe4ef'; fg = '#e91e63'; break; // rosa suave
              case 'comprado': bg = '#e3f2fd'; fg = '#1976d2'; break;
              case 'en reparto': bg = '#fff7e6'; fg = '#f59e0b'; break;
              case 'entregado': bg = '#e8f5e9'; fg = '#388e3c'; break;
              case 'en la puerta': bg = '#fdeaea'; fg = '#d12b39'; break;
              default: bg = '#f5f5f5'; fg = '#555';
            }
            return (
              <span key={estado} style={{
                color: fg,
                background: bg,
                borderRadius: 8,
                padding: '4px 10px',
                border: `1.2px solid ${fg}33`,
                fontWeight: 700,
                textShadow: 'none',
                boxShadow: 'none',
                letterSpacing: '0.5px',
              }}>{estado.charAt(0).toUpperCase() + estado.slice(1)}: {count}</span>
            );
          })}
        </div>
      </div>

      {items.length === 0 && !loading && <p style={{ textAlign:'center', marginTop:30 }}>No hay compras para mostrar.</p>}

      {/* Lista de compras */}
      <div>
        {items.map((compra, index) => {
          const estadoColor = estadoColores[compra.estado] || '#555';
          return (
            <div style={{ border:'1px solid #ddd', borderLeft:`6px solid ${estadoColor}`, borderRadius:10, padding:'12px 14px', marginBottom:10, background:'#fff', boxShadow:'0 2px 6px rgba(0,0,0,0.06)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth:240 }}>
                  <span style={{ fontWeight:700, fontSize:20, color:'#1976d2' }}>{compra.abreviatura_piso}</span>
                  {compra.ruta_piso && (
                    <span
                      style={{
                        background: obtenerColorRuta(compra.ruta_piso),
                        color: getContrastColor(obtenerColorRuta(compra.ruta_piso)),
                        padding: '3px 10px',
                        borderRadius: 14,
                        fontSize: 13,
                        fontWeight: 700,
                        border: `1px solid ${darkenColor(obtenerColorRuta(compra.ruta_piso), 25)}`,
                      }}
                    >
                      Ruta: {compra.ruta_piso}
                    </span>
                  )}
                  {compra.franja_horaria && <span style={{ background:'#ffeef0', color:'#8a0b12', padding:'3px 10px', borderRadius:14, fontSize:13, fontWeight:700, border:'1px solid #ffcdd2' }}>Franja: {compra.franja_horaria}</span>}
                  <span style={{ fontWeight:800, color:estadoColores[compra.estado]||'#555', textTransform:'capitalize', border:`2px solid ${estadoColores[compra.estado]||'#555'}`, padding:'4px 10px', borderRadius:14, fontSize:14, background:'#f7f7f7' }}>{compra.estado}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontWeight:800, fontSize:16, color:'#388e3c' }}>{Number(compra.precio_final||0).toFixed(2)} €</span>
                  {(compra.n_bolsas!=null) && <span style={{ fontWeight:800, fontSize:16, color:'#333' }}>{compra.n_bolsas} 🛍️</span>}
                  <button onClick={()=>descargarCompraPDF(compra)} style={{ border:'none', background:'#1976d2', color:'#fff', width:36, height:36, borderRadius:'50%', cursor:'pointer', fontSize:18, boxShadow:'0 3px 8px rgba(25,118,210,0.35)' }}>📥</button>
                </div>
              </div>
              <div style={{ fontSize:13, color:'#555', marginBottom:10, display:'flex', flexDirection:'column', gap:2, background:'#e9f3fd', borderRadius:8, padding:'6px 10px', fontWeight:600 }}>
                <span>Pedido: <strong>{compra.user_supervisora||'—'}</strong> - {formatearFecha(compra.dia_pedido)}</span>
                {compra.dia_compra && <span style={{ color:'#0d47a1' }}>Comprado: <strong>{compra.user_comprador||'—'}</strong> - {formatearFecha(compra.dia_compra)}</span>}
                {!compra.dia_compra && compra.estado === 'pedido' && <span style={{ color:'#0d47a1' }}>Compra: <strong>{compra.user_comprador||'—'}</strong></span>}
                {compra.dia_recogida && (
                  <span style={{ color:'#ca6f1aff' }}>
                    En reparto: <strong>{compra.user_repartidor||'—'}</strong> - {formatearFecha(compra.dia_recogida)}
                  </span>
                )}
                {!compra.dia_recogida && compra.estado === 'en reparto' && (
                  <span style={{ color:'#ca6f1aff' }}>
                    Preparando reparto... <strong>{compra.user_repartidor||'—'}</strong>
                  </span>
                )}
                {/* Resumen completo entre 'En reparto' y 'Entregado' (todas las líneas) */}
                {Array.isArray(compra.historial_recogida) && compra.historial_recogida.length>0 && (
                  <>
                    {compra.historial_recogida.filter(h=>h.tipo==='swap').map((h,i)=> {
                      const recogio = h.dia_recogida_entrega ? ` (recogió: ${h.dia_recogida_entrega})` : '';
                      return (
                        <span key={i} style={{ color:'#ca6f1aff' }}> {' '}➜ {h.entrega||'—'} ➜ {h.recoge||'—'} - {h.dia||''}{recogio}</span>
                      );
                    })}
                  </>
                )}
                {compra.dia_reparto && <span style={{ color:'#2d7a09' }}>Entregado: <strong>{compra.user_repartidor||'—'}</strong> - {formatearFecha(compra.dia_reparto)}</span>}
              </div>
              <details style={{ fontSize:14 }}>
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: 700,
                    marginBottom: 6,
                    fontSize: 16,
                    color: "#1976d2",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    userSelect: "none",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="#1976d2"
                    style={{ width: 20, height: 20 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M7 6h10M7 18h10" />
                  </svg>
                  Productos
                </summary>
                <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:'0 6px', marginTop:4, fontSize:13, color:'#444' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign:'left', padding:'8px 10px', background:'#f5f5f5', width:'50%' }}>Producto</th>
                      <th style={{ textAlign:'center', padding:'8px 10px', background:'#f5f5f5', width:'15%' }}>Cant.</th>
                      <th style={{ textAlign:'right', padding:'8px 10px', background:'#f5f5f5', width:'20%' }}>Precio unidad</th>
                      <th style={{ textAlign:'right', padding:'8px 10px', background:'#f5f5f5', width:'15%' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(compra.productos||[]).map((producto, idx) => {
                      // Comprobación de producto eliminado (tachado)
                      const cambioEliminado = (compra.cambios || []).find(c => c.id_producto_eliminado === producto.id);
                      if (cambioEliminado) {
                        return (
                          <tr key={idx} style={{ color: "#999" }}>
                            <td style={{ padding: "6px 12px", textDecoration: "line-through" }}>{cambioEliminado.nombre_eliminado}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right", textDecoration: "line-through" }}>{cambioEliminado.cantidad_eliminado}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right", textDecoration: "line-through" }}>{Number(cambioEliminado.precio_eliminado).toFixed(2)} €</td>
                            <td style={{ textDecoration: "line-through", textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
                              {(cambioEliminado.cantidad_eliminado * cambioEliminado.precio_eliminado).toFixed(2)} €
                            </td>
                          </tr>
                        );
                      }

                      // Comprobación de producto añadido (verde) — usar comparación normalizada para evitar fallos por acentos/espacios
                      const cambioAñadido = (compra.cambios || []).find(c => normalizeName(String(c.nombre_añadido)) === normalizeName(String(producto.nombre)));
                      if (cambioAñadido) {
                        return (
                          <tr key={idx} style={{ color: "#2e7d32" }}>
                            <td style={{ padding: "6px 12px" }}>{cambioAñadido.nombre_añadido}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambioAñadido.unidades_añadido}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right" }}>{Number(cambioAñadido.precio_añadido).toFixed(2)} €</td>
                            <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
                              {(cambioAñadido.unidades_añadido * cambioAñadido.precio_añadido).toFixed(2)} €
                            </td>
                          </tr>
                        );
                      }

                      // Render por defecto
                      return (
                        <tr key={idx}>
                          <td style={{ padding:'6px 10px' }}>• {producto.nombre}</td>
                          <td style={{ padding:'6px 10px', textAlign:'right' }}>{producto.cantidad}</td>
                          <td style={{ padding:'6px 10px', textAlign:'right' }}>{Number(producto.precio).toFixed(2)} €</td>
                          <td style={{ padding:'6px 10px', textAlign:'right', fontWeight:700 }}>{(producto.cantidad*producto.precio).toFixed(2)} €</td>
                        </tr>
                      );
                    })}
                    {/* Añadidos que no estén en productos normales (mostrar cambios que no aparecen en la lista original) */}
                    {(compra.cambios || []).map((cambio, idx) => {
                      // Si es un "no sustitución" (id_producto_añadido === 1), no mostrar producto añadido
                      if (cambio.id_producto_añadido === 1) return null;
                      const found = (compra.productos || []).find(p => normalizeName(String(p.nombre)) === normalizeName(String(cambio.nombre_añadido)));
                      if (!found) {
                        return (
                          <tr key={`nuevo-${idx}`} style={{ color: "#2e7d32" }}>
                            <td style={{ padding: "6px 12px" }}>{cambio.nombre_añadido}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambio.unidades_añadido}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right" }}>{Number(cambio.precio_añadido).toFixed(2)} €</td>
                            <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
                              {(cambio.unidades_añadido * cambio.precio_añadido).toFixed(2)} €
                            </td>
                          </tr>
                        );
                      }
                      return null;
                    })}
                  </tbody>
                </table>
              </details>
              {/* Desplegable de cambios */}
              {Array.isArray(compra.cambios) && compra.cambios.length > 0 && (
                <details className="cambios-details" style={{
                  marginTop: 12,
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
                  border: '1.5px solid #ffc107',
                  borderRadius: 10,
                  boxShadow: '0 2px 8px rgba(255,193,7,0.15)',
                }}>
                  <summary style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#856404',
                    marginBottom: 0,
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    userSelect: 'none',
                    listStyle: 'none',
                    paddingLeft: 20,
                  }}>
                    <span style={{ fontSize: 16 }}>⚠️</span>
                    <span>Cambios realizados ({compra.cambios.length})</span>
                  </summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {compra.cambios.map((cambio, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: '8px 12px',
                        background: '#fff',
                        borderRadius: 8,
                        border: '1px solid #e0e0e0',
                        fontSize: 12,
                      }}>
                        {cambio.id_producto_eliminado && cambio.id_producto_eliminado !== 1 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            color: '#d32f2f',
                          }}>
                            <span style={{ fontSize: 14, fontWeight: 700 }}>❌</span>
                            <span style={{ textDecoration: 'line-through', flex: 1 }}>
                              {cambio.nombre_eliminado}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {cambio.cantidad_eliminado} ud.
                            </span>
                            <span style={{ fontWeight: 700 }}>
                              {Number(cambio.precio_eliminado || 0).toFixed(2)} €
                            </span>
                          </div>
                        )}
                        {cambio.id_producto_añadido && cambio.id_producto_añadido !== 1 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            color: '#2e7d32',
                          }}>
                            <span style={{ fontSize: 14, fontWeight: 700 }}>✅</span>
                            <span style={{ flex: 1, fontWeight: 600 }}>
                              {cambio.nombre_añadido}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {cambio.unidades_añadido} ud.
                            </span>
                            <span style={{ fontWeight: 700 }}>
                              {Number(cambio.precio_añadido || 0).toFixed(2)} €
                            </span>
                          </div>
                        )}
                        {cambio.id_producto_añadido === 1 && (
                          <div style={{
                            fontSize: 11,
                            fontStyle: 'italic',
                            color: '#666',
                            marginTop: 2,
                          }}>
                            (No sustituido)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {(compra.notas_supervisora?.trim() || compra.notas_comprador?.trim() || compra.precio_comprador) && (
                <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:10 }}>
                  {compra.notas_supervisora?.trim() && <div style={{ background:'#f3e8ff', border:'1px solid #d8b4fe', borderRadius:8, padding:'8px 12px', color:'#6b21a8', fontSize:13 }}><strong>Notas supervisora:</strong> {compra.notas_supervisora}</div>}
                  {compra.notas_comprador?.trim() && <div style={{ background:'#e3f2fd', border:'1px solid #90caf9', borderRadius:8, padding:'8px 12px', color:'#1565c0', fontSize:13 }}><strong>Notas comprador:</strong> {compra.notas_comprador}</div>}
                  {compra.precio_comprador && <div style={{ background:'#e8f5e9', border:'1px solid #a5d6a7', borderRadius:8, padding:'8px 12px', color:'#2e7d32', fontWeight:700, fontSize:14 }}>Precio compra: {compra.precio_real != null ? Number(compra.precio_real).toFixed(2) : Number(compra.precio_comprador).toFixed(2)} €</div>}
                </div>
              )}
              {compra.ticket && (
                <div style={{ marginTop:16, display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
                  {ticketsVisibles.has(compra.id) ? (
                    <>
                      <AuthImage
                        src={`/api/tickets/${compra.ticket}`}
                        alt='Ticket'
                        style={{ maxWidth:160, cursor:'pointer', borderRadius:8, boxShadow:'0 4px 12px rgba(0,0,0,0.12)' }}
                        onClick={()=> abrirFicheroProtegido(`/api/tickets/${compra.ticket}`)}
                      />
                      <button
                        style={{ padding:'8px 18px', fontSize:15, fontWeight:600, cursor:'pointer', borderRadius:30, border:'none', background:'#1976d2', color:'#fff' }}
                        onClick={async ()=>{
                          try {
                            const url = `/api/tickets/${compra.ticket}`;
                            const r = await fetchProtegido(url);
                            if(!r.ok) throw new Error('Descarga fallida');
                            const b = await r.blob();
                            const u = URL.createObjectURL(b);
                            const a = document.createElement('a');
                            a.href = u;
                            a.download = `ticket_${compra.id}_${compra.abreviatura_piso}.jpg`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                          } catch(err){
                            alert('Error al descargar ticket');
                          }
                        }}
                      >⬇ Descargar ticket</button>
                      <button
                        style={{ padding:'8px 18px', fontSize:15, fontWeight:600, cursor:'pointer', borderRadius:30, border:'none', background:'#e53935', color:'#fff' }}
                        onClick={()=> setTicketsVisibles(prev=>{ const n=new Set(prev); n.delete(compra.id); return n; })}
                      >Ocultar ticket</button>
                    </>
                  ) : (
                    <button
                      style={{ padding:'8px 18px', fontSize:15, fontWeight:600, cursor:'pointer', borderRadius:30, border:'none', background:'#1976d2', color:'#fff' }}
                      onClick={()=> setTicketsVisibles(prev=>{ const n=new Set(prev); n.add(compra.id); return n; })}
                    >Ver ticket</button>
                  )}
                </div>
              )}

              {/* Foto en la puerta (lazy load) */}
              {compra.estado === 'en la puerta' && compra.en_la_puerta_img && (
                <div style={{ marginTop:12, display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
                  {fotosPuertaVisibles.has(compra.id) ? (
                    <>
                      <AuthImage
                        src={`/api/entregas_fotos/${compra.en_la_puerta_img}`}
                        alt='Foto en la puerta'
                        style={{ maxWidth:180, cursor:'pointer', borderRadius:8, boxShadow:'0 4px 12px rgba(0,0,0,0.12)' }}
                        onClick={()=> abrirFicheroProtegido(`/api/entregas_fotos/${compra.en_la_puerta_img}`)}
                      />
                      <button
                        style={{ padding:'8px 18px', fontSize:15, fontWeight:600, cursor:'pointer', borderRadius:30, border:'none', background:'#1976d2', color:'#fff' }}
                        onClick={async ()=>{
                          try {
                            const url = `/api/entregas_fotos/${compra.en_la_puerta_img}`;
                            const r = await fetchProtegido(url);
                            if(!r.ok) throw new Error('Descarga fallida');
                            const b = await r.blob();
                            const u = URL.createObjectURL(b);
                            const a = document.createElement('a');
                            a.href = u;
                            a.download = compra.en_la_puerta_img || `foto_en_puerta_${compra.id}.jpg`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                          } catch(err){
                            alert('Error al descargar la foto');
                          }
                        }}
                      >⬇ Descargar foto</button>
                      <button
                        style={{ padding:'8px 18px', fontSize:15, fontWeight:600, cursor:'pointer', borderRadius:30, border:'none', background:'#e53935', color:'#fff' }}
                        onClick={()=> setFotosPuertaVisibles(prev=>{ const n=new Set(prev); n.delete(compra.id); return n; })}
                      >Ocultar foto</button>
                    </>
                  ) : (
                    <button
                      style={{ padding:'8px 18px', fontSize:15, fontWeight:600, cursor:'pointer', borderRadius:30, border:'none', background:'#1976d2', color:'#fff' }}
                      onClick={()=> setFotosPuertaVisibles(prev=>{ const n=new Set(prev); n.add(compra.id); return n; })}
                    >Ver foto en la puerta</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Paginación (misma apariencia que ModificarVerCompras) */}
      {total > pageSize && (
        <div style={{
          position: 'fixed',
          left: 0,
          bottom: 0,
          width: '100vw',
          background: 'rgba(255,255,255,0.97)',
          borderTop: '2px solid #e0e0e0',
          boxShadow: '0 -2px 12px #0001',
          zIndex: 200,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 15,
          padding: '12px 0',
        }}>
          <button
            onClick={() => { if (page > 1) fetchPage(page - 1); }}
            disabled={page === 1}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              border: '1.5px solid #1976d2',
              background: page === 1 ? '#e3e3e3' : '#1976d2',
              color: page === 1 ? '#888' : '#fff',
              fontWeight: 800,
              cursor: page === 1 ? 'not-allowed' : 'pointer'
            }}
          >◀ Anterior</button>

          <span style={{ color: '#1976d2', fontWeight: 900, letterSpacing: '1px' }}>Página {page} / {totalPages}</span>
          <span style={{ color: '#555', fontWeight: 700 }}>{total} compras</span>

          <button
            onClick={() => { if (page < totalPages) fetchPage(page + 1); }}
            disabled={page === totalPages}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              border: '1.5px solid #1976d2',
              background: page === totalPages ? '#e3e3e3' : '#1976d2',
              color: page === totalPages ? '#888' : '#fff',
              fontWeight: 800,
              cursor: page === totalPages ? 'not-allowed' : 'pointer'
            }}
          >Siguiente ▶</button>
        </div>
      )}
      {loading && <p style={{ textAlign:'center', marginTop:10 }}>Cargando...</p>}
    </div>
  );
};

export default VerComprasAdmin;
